import React, { useCallback } from "react";
import { PauseIcon, PlayIcon, ReloadIcon, StopIcon, VolumeHighIcon } from "hugeicons-react";
import { useAudio } from "../../contexts/AudioContext";
import { useSettings } from "../../contexts/SettingsContext";
import { useToast } from "../../contexts/ToastContext";
import { stripMessageContent } from "../../helpers/chatHelper";
import { DropdownMenuRow } from "../ui/dropdown-menu";

interface MessageTTSMenuItemsProps {
  messageId: string;
  messageContent: string;
  isEmpty: boolean;
}

export const MessageTTSMenuItems: React.FC<MessageTTSMenuItemsProps> = ({
  messageId,
  messageContent,
  isEmpty,
}) => {
  const {
    stopAudio,
    speak,
    currentAudioMessageId,
    isMessageConverting,
    isPlaying,
    isPaused,
    hasFinished,
    pauseAudio,
    resumeAudio,
  } = useAudio();
  const { activeVoice } = useSettings();
  const toast = useToast();

  const isThisMessage = currentAudioMessageId === messageId;
  const canSpeak = Boolean(activeVoice) && !isEmpty;

  // Runs synchronously from the menu click so the service can unlock audio playback.
  // Generation and playback failures are toasted by the audio provider.
  const handlePlayTTS = useCallback(() => {
    const content = stripMessageContent(messageContent);

    if (!content?.trim()) {
      toast("There's Nothing to Read Aloud in This Message.", "warning");
      return;
    }

    if (!activeVoice) {
      toast("Select a Voice in Settings First.", "warning");
      return;
    }

    void speak(content, messageId, activeVoice);
  }, [activeVoice, messageContent, messageId, speak, toast]);

  if (isMessageConverting && isThisMessage) {
    return (
      <DropdownMenuRow
        disabled
        className="message-tts-menu-item message-tts-menu-item--status"
        icon={<div className="audio-converting-spinner" aria-hidden />}
        text="Generating Audio"
      />
    );
  }

  if (isThisMessage && isPlaying) {
    return (
      <>
        <DropdownMenuRow
          icon={hasFinished ? <ReloadIcon /> : isPaused ? <PlayIcon /> : <PauseIcon />}
          text={hasFinished ? "Replay" : isPaused ? "Resume" : "Pause"}
          action={() => {
            if (isPaused) {
              resumeAudio();
            } else {
              pauseAudio();
            }
          }}
        />
        <DropdownMenuRow
          icon={<StopIcon />}
          text="Stop"
          action={stopAudio}
        />
      </>
    );
  }

  return (
    <DropdownMenuRow
      disabled={!canSpeak}
      title={!activeVoice ? "Select a voice in settings first" : isEmpty ? "No text to read" : undefined}
      icon={<VolumeHighIcon />}
      text="Read Aloud"
      action={handlePlayTTS}
    />
  );
};
